import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { AssistantLink, AssistantSettingsResponse } from '@aeris/contracts'
import {
  Button,
  CheckboxField,
  ConfirmDialog,
  DateField,
  Field,
  InlineAlert,
  Input,
  QueryState,
  Section,
  SwitchField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  fetchAssistantExchanges,
  fetchAssistantLinks,
  fetchAssistantSettings,
  removeAssistantLink,
  setAssistantLinkUnlimited,
  saveAssistantSettings,
} from '../lib/api.ts'
import { formatDayTime } from '../lib/date-format.ts'

/**
 * O WhatsApp do assistente, do lado do dono (F254, F256).
 *
 * Desde a F256 o número e o GPT são do Aeris, iguais para todas as contas: a
 * loja só liga, aceita o envio dos números dela à OpenAI pelo Aeris, escolhe
 * o limite de perguntas por pessoa e os horários do resumo, e vê quem está
 * ligado, o que perguntaram e o gasto do mês (o teto quem define é o Aeris).
 */

const STATUS_LABELS: Readonly<Record<string, string>> = {
  queued: 'na fila',
  answering: 'respondendo',
  answered: 'respondida',
  failed: 'falhou',
  refused: 'recusada',
}

const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const
const DAY_NAMES = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
] as const
const MAX_DIGEST_TIMES = 6

const SettingsForm = ({
  data,
}: {
  readonly data: AssistantSettingsResponse
}): ReactNode => {
  const queryClient = useQueryClient()
  const settings = data.settings
  const [enabled, setEnabled] = useState(settings.enabled)
  const [perHour, setPerHour] = useState(
    String(settings.questionsPerUserPerHour)
  )
  const [consent, setConsent] = useState(settings.consentAt !== null)
  const [times, setTimes] = useState<readonly string[]>(settings.digestTimes)
  const [days, setDays] = useState<readonly number[]>(settings.digestDays)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      await saveAssistantSettings({
        enabled,
        questionsPerUserPerHour: Number(perHour),
        consent,
        // O campo de hora apagado não vira um horário.
        digestTimes: times.filter((time) => /^\d{2}:\d{2}$/.test(time)),
        digestDays: [...days],
        version: settings.version,
      })
    },
    onSuccess: async () => {
      setError(null)
      notify.success(enabled ? 'Assistente ligado.' : 'Configuração salva.')
      await queryClient.invalidateQueries({ queryKey: ['assistant-settings'] })
    },
    onError: (failure: unknown) => {
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível salvar. Tente de novo.'
      )
    },
  })

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (!save.isPending) save.mutate()
      }}
    >
      {data.assistantPhone === null ? (
        <InlineAlert tone="warning">
          O número do assistente do Aeris ainda não está no ar. Dá para
          configurar agora; as perguntas começam quando ele estiver.
        </InlineAlert>
      ) : (
        <p className="text-(length:--text-sm)">
          O número do assistente é <strong>{data.assistantPhone}</strong>, do
          Aeris One — o mesmo para todas as lojas. Cada pessoa liga o próprio
          WhatsApp em Meu assistente.
        </p>
      )}
      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      <Field label="Perguntas por pessoa por hora">
        {(field) => (
          <Input
            {...field}
            inputMode="numeric"
            value={perHour}
            onChange={(event) => {
              setPerHour(event.target.value.replace(/\D/g, ''))
            }}
          />
        )}
      </Field>

      <CheckboxField
        label="Aceito que os números da loja (vendas, vendedores, clientes que mais compram, contas a receber) sejam enviados à OpenAI pelo Aeris One para responder as perguntas."
        checked={consent}
        onCheckedChange={(checked) => {
          setConsent(checked === true)
          if (checked !== true) setEnabled(false)
        }}
      />

      <fieldset className="flex flex-col gap-2">
        <legend className="text-(length:--text-sm) font-(--weight-medium)">
          Resumo do dia no WhatsApp
        </legend>
        <p className="text-(length:--text-xs) text-(--text-muted)">
          Vai para quem ligou o WhatsApp e não pediu para parar, no relógio da
          loja. O último horário é o fechamento: leva também o que ficou para
          amanhã (a receber vencido, estoque abaixo do mínimo, caixas abertos).
          Sem horário, ninguém recebe.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {times.map((time, index) => (
            // A posição é a identidade do horário: a lista só muda por uma
            // ação de quem edita (adicionar, remover).
            <div key={String(index)} className="flex items-center gap-1">
              <DateField
                type="time"
                aria-label={`Horário ${String(index + 1)} do resumo`}
                value={time}
                onChange={(event) => {
                  const next = [...times]
                  next[index] = event.target.value
                  setTimes(next)
                }}
                className="w-32"
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remover o horário ${time === '' ? String(index + 1) : time}`}
                onClick={() => {
                  setTimes(times.filter((_, other) => other !== index))
                }}
              >
                Remover
              </Button>
            </div>
          ))}
          {times.length >= MAX_DIGEST_TIMES ? null : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setTimes([...times, '17:00'])
              }}
            >
              Adicionar horário
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {DAY_LABELS.map((label, day) => (
            <CheckboxField
              key={label}
              label={label}
              aria-label={DAY_NAMES[day]}
              checked={days.includes(day)}
              onCheckedChange={(checked) => {
                setDays(
                  checked === true
                    ? [...days, day].sort((a, b) => a - b)
                    : days.filter((other) => other !== day)
                )
              }}
            />
          ))}
        </div>
      </fieldset>

      <SwitchField
        label="Assistente ligado"
        description="Desligado, o número responde que o assistente não está ligado."
        checked={enabled}
        disabled={!consent}
        onCheckedChange={setEnabled}
      />

      <p className="text-(length:--text-xs) text-(--text-muted)">
        Gasto do assistente neste mês: US$ {data.spentMonthUsd} de US${' '}
        {data.monthlyCapUsd}. Passando do limite, ele para de responder até o
        mês que vem.
      </p>

      <div>
        <Button type="submit" size="sm" loading={save.isPending}>
          Salvar
        </Button>
      </div>
    </form>
  )
}

const Links = (): ReactNode => {
  const queryClient = useQueryClient()
  const links = useQuery({
    queryKey: ['assistant-links'],
    queryFn: fetchAssistantLinks,
  })
  const [removing, setRemoving] = useState<AssistantLink | null>(null)
  const remove = useMutation({
    mutationFn: async (id: string) => {
      await removeAssistantLink(id)
    },
    onSuccess: async () => {
      setRemoving(null)
      notify.success('WhatsApp desligado do assistente.')
      await queryClient.invalidateQueries({ queryKey: ['assistant-links'] })
    },
  })
  const unlimited = useMutation({
    mutationFn: async (input: { id: string; on: boolean }) => {
      await setAssistantLinkUnlimited(input.id, input.on)
    },
    onSuccess: async (_data, input) => {
      notify.success(
        input.on
          ? 'Este número pergunta sem limite por hora.'
          : 'Este número volta ao limite por hora.'
      )
      await queryClient.invalidateQueries({ queryKey: ['assistant-links'] })
    },
  })
  const list = links.data?.links ?? []

  return (
    <Section
      title="Quem está ligado"
      description="Cada pessoa liga o próprio WhatsApp em Meu assistente, com um código."
    >
      <QueryState
        isPending={links.isPending}
        isError={links.isError}
        error={links.error}
        onRetry={() => void links.refetch()}
        what="quem está ligado"
        isEmpty={list.length === 0}
        empty={
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Ninguém ligou o WhatsApp ainda.
          </p>
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Quem</TableHeaderCell>
                <TableHeaderCell>WhatsApp</TableHeaderCell>
                <TableHeaderCell>Resumo do dia</TableHeaderCell>
                <TableHeaderCell>Limite por hora</TableHeaderCell>
                <TableHeaderCell>Última mensagem</TableHeaderCell>
                <TableHeaderCell>
                  <span className="sr-only">Ações</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((link) => (
                <TableRow key={link.id}>
                  <TableCell>
                    {/* F276: o nome de quem usa o número, e de qual conta. */}
                    {link.userName ?? '—'}
                    {link.personName !== null &&
                    link.accountName !== null &&
                    link.accountName !== link.personName
                      ? ` (conta ${link.accountName})`
                      : ''}
                  </TableCell>
                  <TableCell>{link.phone ?? '—'}</TableCell>
                  <TableCell>
                    {link.receivesDigest ? 'recebe' : 'não recebe'}
                  </TableCell>
                  <TableCell>
                    {/* F276: o dono libera quem precisa testar; o teto do mês continua. */}
                    <SwitchField
                      label="Sem limite"
                      checked={link.hourlyUnlimited}
                      disabled={unlimited.isPending}
                      onCheckedChange={(checked) => {
                        unlimited.mutate({ id: link.id, on: checked })
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {link.lastMessageAt === null
                      ? '—'
                      : formatDayTime(link.lastMessageAt)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setRemoving(link)
                      }}
                    >
                      Desligar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>
      <ConfirmDialog
        open={removing !== null}
        title="Desligar este WhatsApp do assistente?"
        description={
          removing === null
            ? ''
            : `${removing.userName ?? 'A pessoa'} para de receber o resumo e o assistente não responde mais este WhatsApp.`
        }
        confirmLabel="Desligar"
        tone="danger"
        loading={remove.isPending}
        onConfirm={async () => {
          if (removing !== null) await remove.mutateAsync(removing.id)
        }}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
      />
    </Section>
  )
}

const History = (): ReactNode => {
  const exchanges = useQuery({
    queryKey: ['assistant-exchanges'],
    queryFn: async () => await fetchAssistantExchanges(1),
  })
  const items = exchanges.data?.items ?? []
  return (
    <Section
      title="Perguntas"
      description="O que perguntaram ao assistente pelo WhatsApp, e o que ele respondeu."
    >
      <QueryState
        isPending={exchanges.isPending}
        isError={exchanges.isError}
        error={exchanges.error}
        onRetry={() => void exchanges.refetch()}
        what="as perguntas"
        isEmpty={items.length === 0}
        empty={
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Ninguém perguntou nada ainda.
          </p>
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Quando</TableHeaderCell>
                <TableHeaderCell>Quem</TableHeaderCell>
                <TableHeaderCell>Pergunta</TableHeaderCell>
                <TableHeaderCell>Resposta</TableHeaderCell>
                <TableHeaderCell>Custo (US$)</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{formatDayTime(item.createdAt)}</TableCell>
                  <TableCell>{item.userName ?? '—'}</TableCell>
                  <TableCell className="max-w-xs whitespace-normal">
                    {item.question}
                  </TableCell>
                  <TableCell className="max-w-md whitespace-pre-line">
                    {item.answer ?? STATUS_LABELS[item.status] ?? item.status}
                  </TableCell>
                  <TableCell>{item.costUsd}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>
    </Section>
  )
}

export const AssistantSettings = (): ReactNode => {
  const settings = useQuery({
    queryKey: ['assistant-settings'],
    queryFn: fetchAssistantSettings,
  })
  return (
    <>
      <Section
        title="WhatsApp do assistente"
        description="O dono e os gerentes perguntam os números da loja por um número só do assistente; o GPT da loja responde consultando o Aeris."
      >
        <QueryState
          isPending={settings.isPending}
          isError={settings.isError}
          error={settings.error}
          onRetry={() => void settings.refetch()}
          what="a configuração do assistente"
          isEmpty={false}
          empty={null}
        >
          {settings.data === undefined ? null : (
            <SettingsForm
              key={settings.data.settings.version ?? 'nova'}
              data={settings.data}
            />
          )}
        </QueryState>
      </Section>
      <Links />
      <History />
    </>
  )
}
